import State from './State'
import Log from './Log'
import Network from './Network'
import Conf from './Conf'
import mitt from 'mitt'

let emitter = null
let prefix = ''
let stack = []
let currentQuestion = null
let currentSurveyId = null
let currentSurveyData = {}
let backupSurveyConf = null
let stateKey = 'messages'

export default class Survey {
  static getCurrentState(value) {
    return {
      currentQuestion,
      currentSurveyId,
      stack,
      prefix,
      value
    }
  }

  static setStateKey(newVal) {
    stateKey = newVal
  }

  static getStateKey() {
    return stateKey
  }

  static async editAt(data) {
    backupSurveyConf = {
      currentQuestion,
      currentSurveyId,
      stack,
      prefix
    }

    Log.info(`Reset at ${data.currentQuestion} in survey ${data.currentSurveyId}.`)

    currentQuestion = data.currentQuestion
    currentSurveyId = data.currentSurveyId
    stack = data.stack
    prefix = data.prefix
    currentSurveyData = await this.loadSurvey(currentSurveyId)

    State.setDependencies(currentSurveyData.dependencies)
    State.setQuestions(currentSurveyData.questions)
    State.refreshDependencies()

    this.emitStepData(data.value)
  }

  static async cancelEdit() {
    Log.info(`Coming back to ${backupSurveyConf.currentQuestion} in survey ${backupSurveyConf.currentSurveyId}.`)

    currentQuestion = backupSurveyConf.currentQuestion
    currentSurveyId = backupSurveyConf.currentSurveyId
    stack = backupSurveyConf.stack
    prefix = backupSurveyConf.prefix
    backupSurveyConf = null
    currentSurveyData = await this.loadSurvey(currentSurveyId)

    State.setDependencies(currentSurveyData.dependencies)
    State.setQuestions(currentSurveyData.questions)
    State.refreshDependencies()
    State.removeEditStepMessages()

    await this.updateState()
    this.emitStepData()
  }

  static async init(firstSurveyId, onStep, newStateKey) {
    emitter = mitt()
    stateKey = newStateKey
    this.on('step', onStep)

    let mode = await State.get('mode', 'index')
    let surveyId = await State.get(`${mode}CurrentSurvey`, null)
    if (surveyId === null) {
      Log.info(
        `No survey in progress, choosing to load survey ${firstSurveyId}.`
      )
      this.start(firstSurveyId)
    } else {
      Log.info(`Survey ${surveyId} is running.`)
      this.initRunningSurvey(surveyId)
    }
  }

  static on(eventName, cb) {
    emitter.on(eventName, cb)
  }

  static off(eventName, cb) {
    emitter.off(eventName, cb)
  }

  static async loadSurvey(surveyId) {
    if (surveyId === 'livechat') {
      return {
        dependencies: {
          needRestart: {
            operator: 'AND',
            children: [{
              question: 'again',
              answer: 'yes'
            }]
          }
        },
        externalDependencies: [],
        name: "Livechat",
        questions: {
          intro: {
            first: true,
            type: "textarea",
            name: "Intro",
            question: Conf.get().livechat.firstQuestion,
            attributes: {
              pattern: "/.*/",
              min: 0,
              max: 99999999,
              placeholder: "",
              ignoreEnterKey: true
            },
            $survey: {
              id: 'livechat',
              name: 'Livechat'
            },
            next: 'livechat'
          },
          livechat: {
            name: 'Livechat',
            type: 'livechat',
            isLivechatOnly: true,
            ask_email: Conf.get().livechat.ask_email,
            ask_firstname: Conf.get().livechat.ask_firstname,
            ask_lastname: Conf.get().livechat.ask_lastname,
            email_required: Conf.get().livechat.email_required,
            handle_timeout: Conf.get().livechat.handle_timeout,
            phone_number: Conf.get().livechat.phone_number,
            email_address: Conf.get().livechat.email_address,
            chatbot_line: Conf.get().livechat.chatbot_line,
            $survey: {
              id: 'livechat',
              name: 'Livechat'
            },
            next: 'again'
          },
          again: {
            type: "radio",
            name: "again",
            question: {
              fr: 'Merci d\'avoir échangé avec nous, avez-vous besoin de rediscuter avec nous ?'
            },
            answers: [
              {
                name: 'Oui',
                slug: 'yes'
              },
              {
                name: 'Non merci',
                slug: 'no'
              }
            ],
            $survey: {
              id: 'livechat',
              name: 'Livechat'
            },
            next: [
              {
                question: 'reboot',
                dependsOn: [
                  'needRestart'
                ]
              }
            ]
          },
          reboot: {
            type: "reset",
            name: "reboot",
            $survey: {
              id: 'livechat',
              name: 'Livechat'
            },
          },
        }
      }
    }

    Log.info(`Loading survey ${surveyId} ...`)
    const surveyData = await Network.getInstance().get(
      `${Conf.get().services.config}/customer/${Conf.get().customerId
      }/survey/${surveyId}?chatbotId=${Conf.get().chatbotId}&prefix=${encodeURIComponent(prefix)}`
    )
    if (!surveyData) {
      Log.error(`Cannot load survey ${surveyId}.`)
      return null
    }
    Log.info(`Survey ${surveyId} loaded.`)
    return surveyData.survey ?? null
  }

  static async start(surveyId) {
    // Reset
    prefix = ''
    stack = []
    currentSurveyId = surveyId
    currentQuestion = null
    currentSurveyData = await this.loadSurvey(surveyId)

    State.setDependencies(currentSurveyData.dependencies)
    State.setQuestions(currentSurveyData.questions)
    State.refreshDependencies()

    // Find first question and resume bot
    const first = Object.entries(currentSurveyData.questions).find(
      question => question[1].first
    )
    if (first) {
      currentQuestion = first[0]
      Log.info(`Survey first question is ${first[0]}.`)
    } else {
      Log.info('Survey has no first question.')
    }

    await this.updateState()
    this.emitStepData()
  }

  static async initRunningSurvey(surveyId) {
    // Init conf values
    const mode = await State.get('mode', 'index')
    prefix = await State.get(`${mode}CurrentPrefix`, '')
    stack = await State.get(`${mode}SurveyStack`, [])
    currentSurveyId = surveyId
    currentQuestion = await State.get(`${mode}CurrentQuestion`, null)
    currentSurveyData = await this.loadSurvey(surveyId)

    State.setDependencies(currentSurveyData.dependencies)
    State.setQuestions(currentSurveyData.questions)
    State.refreshDependencies()

    this.emitStepData()
  }

  static emitStepData(value = null) {
    emitter.emit('step', {
      name: currentQuestion,
      conf: this.getCurrentQuestion(),
      value
    })
  }

  static getCurrentQuestion() {
    return currentSurveyData?.questions[currentQuestion]
  }

  static getCurrentQuestionName() {
    return currentQuestion
  }

  static async updateState() {
    let o = {}
    const mode = await State.get('mode', 'index')
    o[`${mode}CurrentQuestion`] = currentQuestion
    o[`${mode}CurrentPrefix`] = prefix
    o[`${mode}CurrentSurvey`] = currentSurveyId
    o[`${mode}SurveyStack`] = stack
    await State.setMany(o)
  }

  static async gotoSurvey(surveyId, next) {
    stack.push({
      surveyId: currentSurveyId,
      prefix,
      next
    })

    prefix += (prefix.length ? '-' : '') + currentQuestion
    currentSurveyId = surveyId
    currentSurveyData = await this.loadSurvey(surveyId)

    State.setDependencies(currentSurveyData.dependencies)
    State.setQuestions(currentSurveyData.questions)
    State.refreshDependencies()

    // Find first question and resume bot
    const first = Object.entries(currentSurveyData.questions).find(
      question => question[1].first
    )
    if (first) {
      currentQuestion = first[0]
      Log.info(`Survey first question is ${first[0]}.`)
    } else {
      Log.info('Survey has no first question.')
      await this.handleNoNextQuestion()
    }

    await this.updateState()
    this.emitStepData()
  }

  static async endNow() {
    currentQuestion = null
    await this.handleNoNextQuestion()
    await this.updateState()
    this.emitStepData()
  }

  static async handleNoNextQuestion() {
    if (stack.length === 0) {
      await State.set('completion', 'done')
      return
    }

    const stackItem = stack.pop()
    prefix = stackItem.prefix
    currentSurveyId = stackItem.surveyId
    currentSurveyData = await this.loadSurvey(stackItem.surveyId)

    State.setDependencies(currentSurveyData.dependencies)
    State.setQuestions(currentSurveyData.questions)
    State.refreshDependencies()

    const next = await this.getNextQuestionName(stackItem.next)
    if (next === undefined || currentSurveyData.questions[next] === undefined) {
      Log.info('There is no other question.')
      currentQuestion = null
      await this.handleNoNextQuestion()
      await this.updateState()
      this.emitStepData()
      return true
    }

    Log.info(`Going to next question: ${next}`)
    currentQuestion = next
    await this.updateState()
    this.emitStepData()
    return true
  }

  static async next() {
    const question = currentSurveyData?.questions[currentQuestion]
    if (!question) {
      return false
    }

    const next = await this.getNextQuestionName(question.next)
    if (next === undefined || currentSurveyData.questions[next] === undefined) {
      Log.info('There is no other question.')
      currentQuestion = null
      await this.handleNoNextQuestion()
      await this.updateState()
      this.emitStepData()
      return true
    }

    Log.info(`Going to next question: ${next}`)
    currentQuestion = next
    await this.updateState()
    this.emitStepData()
    return true
  }

  static async getNextQuestionName(next) {
    if (next === undefined) {
      return null
    }

    // If only one next question, without dependency, load it directly
    if (typeof next === 'string') {
      return next
    }

    /**
     * Find the next question if :
     * - There is no dependency
     * - All the dependencies are resolved and true
     */
    let nextQuestion = undefined
    for (const q of next) {
      Log.debug('Testing link :', q)
      if (q.dependsOn === undefined) {
        nextQuestion = q
        break
      }
      Log.debug(await State.resolveDependency(q.dependsOn))
      if (await State.resolveDependency(q.dependsOn)) {
        nextQuestion = q
        break
      }
    }

    if (nextQuestion === undefined) {
      return null
    }

    return nextQuestion.question
  }
}
